import { Box, Typography } from '@material-ui/core'
import ExpandLess from '@material-ui/icons/ExpandLess'
import ExpandMore from '@material-ui/icons/ExpandMore'
import { Link, useSearchParams } from 'react-router-dom'
import { useUpdateEffect } from 'usehooks-ts'

import Button from '@/components/button'
import { useGetActivities } from '@/hooks/queries/use-get-activities'
import { routes } from '@/routes/config'
import { CultureLabel } from '@/types/culture'
import { TreatmentLabel } from '@/types/treatment'

import { useActivitiesCalendar } from '../../contexts/activities-calendar-context'
import {
  activityStatus,
  labelActivityType,
  lengthOfDayInPx,
  totalDays,
} from '../../utils/constants'
import { LoadingCalendar } from '../loading-calendar'
import { TodayOverlay } from '../today-overlay'
import { useStyles } from './styles'

export function DiagnosisActivitiesList() {
  const classes = useStyles()

  const [searchParams, setSearchParams] = useSearchParams()

  const page = searchParams.get('page') ? Number(searchParams.get('page')) : 1

  const { dateRange, currentDate } = useActivitiesCalendar()

  const { data } = useGetActivities({
    dateRange,
    page,
  })

  function handleFetchMore() {
    setSearchParams((prev) => {
      prev.set('page', String(page + 1))

      return prev
    })
  }

  function handleFetchLess() {
    setSearchParams((prev) => {
      prev.set('page', String(page - 1))
      return prev
    })
  }

  useUpdateEffect(() => {
    setSearchParams((prev) => {
      prev.set('page', String(1))
      return prev
    })
  }, [currentDate])

  if (!data) {
    return <LoadingCalendar />
  }

  if (data.results.length === 0) {
    return (
      <>
        <TodayOverlay />
        <div className={classes.emptyContainer}>
          <Typography variant='subtitle2'>Calendário de atividades</Typography>
          <Typography>
            Você não possui atividades nesse período, crie uma nova atividade
            cadastrando um novo diagnóstico ou agendando uma nova atividade.
          </Typography>
        </div>
      </>
    )
  }

  return (
    <>
      <TodayOverlay />
      <Typography variant={'h3'}>Diagnósticos</Typography>

      <div className={classes.container}>
        <section>
          {!!data.meta.prev && (
            <div className={classes.previousDiagnosisContainer}>
              <Button
                outlined
                className={classes.fetchMoreButton}
                onClick={handleFetchLess}
              >
                <ExpandLess color='inherit' />
                Diagnósticos mais antigos
              </Button>
            </div>
          )}
          {data.results?.map((diagnosis) => (
            <div key={diagnosis.id} className={classes.diagnosisItem}>
              <div className={classes.description}>
                <Typography variant={'subtitle1'}>
                  {diagnosis.diagnosis_name}
                </Typography>
                {diagnosis.area && (
                  <Typography>
                    <strong>Área: </strong> {diagnosis.area.toFixed(2)} ha
                  </Typography>
                )}
                <Typography>
                  <strong>Cultura: </strong>
                  {CultureLabel[diagnosis.culture]}
                </Typography>
                <Typography>
                  <strong>Diagnóstico: </strong>
                  {TreatmentLabel[diagnosis.treatment]}
                </Typography>
              </div>
              <Box
                padding={'1rem'}
                width={`${totalDays * lengthOfDayInPx + 12}px`}
              >
                {diagnosis.activities.map((activity) => {
                  const Wrapper: React.FC<{
                    className: string
                    style: React.HTMLAttributes<HTMLElement>['style']
                  }> =
                    activity.type === 'pulverization'
                      ? (props) => (
                          <Link
                            {...props}
                            to={routes.ConfirmPulverizationActivity(
                              activity.activity_id,
                            )}
                          />
                        )
                      : (props) => <div {...props} />

                  return (
                    <Wrapper
                      key={activity.activity_id}
                      className={classes.activity}
                      style={{
                        marginLeft: `${
                          activity.range.start * lengthOfDayInPx
                        }px`,
                        width: `${
                          (1 + activity.range.end - activity.range.start) *
                          lengthOfDayInPx
                        }px`,
                        backgroundColor:
                          activityStatus[activity.status].backgroundColor,
                        borderColor:
                          activityStatus[activity.status].borderColor,
                      }}
                    >
                      <div>
                        {labelActivityType[activity.type].icon}
                        <Typography>
                          {labelActivityType[activity.type].label}
                        </Typography>
                      </div>
                    </Wrapper>
                  )
                })}
              </Box>
            </div>
          ))}
          {!!data.meta.next && (
            <div className={classes.nextDiagnosisContainer}>
              <Button
                outlined
                className={classes.fetchMoreButton}
                onClick={handleFetchMore}
              >
                <ExpandMore color='inherit' />
                Diagnósticos mais recentes
              </Button>
            </div>
          )}
        </section>
      </div>
    </>
  )
}
